import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { intersectionBy } from 'lodash';
import { Subject, switchMap } from 'rxjs';

import { App, DashboardFilter, FilterMenuOptions, Project } from '../../models';
import { SNACKBAR_CONSTANTS } from '../../shared/constants';
import { FieldType, SiteFieldsNumericIds } from '../../shared/enums';
import { DashboardsFilterDataService } from '../data-services';
import { DragAndDropFieldSelectionUtilityService } from '../utility-services';
import { FieldsLogicService } from './fields-logic.service';
import { DashboardsFiltersLogicServiceModel, Field } from './models';

@Injectable({
    providedIn: 'root'
})
export class DashboardsFiltersLogicService {

    private _dashboardFiltersValuesLoaded = new Subject<boolean>();
    private _dashboardFilteredSitesLoaded = new Subject<boolean>();
    private _dashboardFiltersCleared = new Subject<void>();

    dashboardFiltersValuesLoaded$ = this._dashboardFiltersValuesLoaded.asObservable();
    dashboardFilteredSitesLoaded$ = this._dashboardFilteredSitesLoaded.asObservable();
    dashboardFiltersCleared$ = this._dashboardFiltersCleared.asObservable();

    model = new DashboardsFiltersLogicServiceModel();

    get availableFields(): Field[] {
        return this.model.availableFields;
    }

    private set availableFields(inAvailableFields: Field[]) {
        this.model.availableFields = inAvailableFields;
    }

    get allFields(): Field[] {
        return this.model.dashboardFilterMenuOptions.allFields;
    }

    get selectedFields(): Field[] {
        return this.model.dashboardFilterMenuOptions.selectedFields;
    }

    private set selectedFields(inSelectFields: Field[]) {
        if (inSelectFields?.length) {
            this.model.dashboardFilterMenuOptions.selectedFields = [...inSelectFields];
            if (this.allFields?.length) {
                this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateFields(this.model.dashboardFilterMenuOptions.selectedFields, this.allFields);
                this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableFields(this.model.dashboardFilterMenuOptions.selectedFields, this.model.availableFields);
                this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;
            }
            else {
                this.model.availableFields = [];
            }
        }
        else {
            this.model.availableFields = [...this.allFields];
            this.model.dashboardFilterMenuOptions.selectedFields = [];
            this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;
        }

        this.model.dashboardsFiltersForm.patchValue({
            selectedFields: inSelectFields,
        });
    }

    constructor(
        private _dashboardsFilterDataService: DashboardsFilterDataService,
        private _dragAndDropFieldSelectionUtilityService: DragAndDropFieldSelectionUtilityService,
        private _fieldsLogicService: FieldsLogicService,
        private _snackBar: MatSnackBar,
        private _formBuilder: UntypedFormBuilder,
    ) { }

    initModel(): void {
        this.model = new DashboardsFiltersLogicServiceModel();
        this.model.dashboardFilterMenuOptions = new FilterMenuOptions();
        this.model.dashboardFilterMenuOptions.selectedFields = Array<Field>();
        this.model.availableFields = Array<Field>();
        this.model.dashboardFilterMenuOptions.allFields = Array<Field>();
        this.model.dashboardsFiltersForm = this._formBuilder.group({
            id: new UntypedFormControl(),
            projectId: new UntypedFormControl(),
            selectedFields: ([])
        });
        this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = [];
    }

    loadModel(incomingFilter: DashboardFilter): void {
        this.model.currentFilter.id = incomingFilter.id;
        this.model.currentFilter.projectId = incomingFilter.projectId;
        this.selectedFields = incomingFilter.selectedFields;
    }

    loadForm(): void {
        this.model.dashboardsFiltersForm.patchValue({
            id: this.model.currentFilter.id,
            projectId: this.model.currentFilter.projectId,
            selectedFields: this.model.dashboardFilterMenuOptions.selectedFields
        });
    }

    saveFilter(app: App, project: Project): void {
        if (!this.model.dashboardsFiltersForm.value.id) {
            this.createDashboardFilter(app, project);
        }
        else {
            this.updateDashboardFilter(app, project);
        }
    }

    createDashboardFilter(app: App, project: Project): void {
        this._dashboardsFilterDataService.createDashboardsFilter(app, project, this.model.dashboardsFiltersForm.value)
            .subscribe({
                next: (savedFilter: DashboardFilter) => {
                    if (savedFilter?.id) {
                        this.loadModel(savedFilter);
                        this.loadForm();
                    }
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard Filter', 'saved'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard Filter', 'saving'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    updateDashboardFilter(app: App, project: Project): void {
        this._dashboardsFilterDataService.updateDashboardsFilter(app, project, this.model.dashboardsFiltersForm.value)
            .subscribe({
                next: (savedFilter: DashboardFilter) => {
                    if (savedFilter?.id) {
                        this.loadModel(savedFilter);
                        this.loadForm();
                    }
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard Filter', 'saved'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard Filter', 'saving'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    getDashboardsFilterByProjectId(app: App, project: Project): void {
        this.isLoadingValues(true);
        this._dashboardsFilterDataService.getDashboardsFilterByProjectId(app, project)
            .pipe(
                switchMap((dashboardsFilter: DashboardFilter) => {
                    if (dashboardsFilter != null) {
                        this.loadModel(dashboardsFilter);
                        this.loadForm();
                        return this._fieldsLogicService.getGridFieldsToFields(app, project);
                    }
                    else {
                        this.initModel();
                        this.selectedFields = new Array<Field>();
                        this.model.dashboardsFiltersForm.patchValue({
                            projectId: project.id,
                        });
                        return this._fieldsLogicService.getGridFieldsToFields(app, project);
                    }
                })
            )
            .subscribe({
                next: (unfilteredFields: Field[]) => {
                    this.setAllFields(this.filterAvailableFieldsForFilter(unfilteredFields), app.aliases.site.singular);
                    if (this.selectedFields.length > 0) {
                        this.getDashboardFilterValuesByProjectId(app, project, this.selectedFields);
                    }
                    else {
                        this._dashboardFiltersValuesLoaded.next(true);
                    }

                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard Filters', 'loaded'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard Filters', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    getMapFilterMenuOptions(app: App, project: Project): void {
        if (project?.id === undefined) {
            return;
        }

        const filterMenuRequest$ = project.isSummaryProject === true
            ? this._dashboardsFilterDataService.getSummaryProjectDashboardsFilterMenu(app, project)
            : this._dashboardsFilterDataService.getDashboardsFilterMenuByProjectId(app, project);

        filterMenuRequest$
            .subscribe({
                next: (mapFilterMenuOptions: FilterMenuOptions) => {
                    if (mapFilterMenuOptions) {
                        mapFilterMenuOptions.allFields = mapFilterMenuOptions.selectedFields;
                        this.model.dashboardFilterMenuOptions = mapFilterMenuOptions;
                    }
                    else {
                        this.model.dashboardFilterMenuOptions = new FilterMenuOptions();
                    }
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard Filters', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    getDashboardFilterValuesByProjectId(currentApp: App, currentProject: Project, fieldsValuesToFetch: Field[]): void {
        this.isLoadingValues(true);
        const filterValuesRequest$ = currentProject.isSummaryProject === true
            ? this._dashboardsFilterDataService.getSummaryProjectDashboardFilterValues(currentApp, currentProject, fieldsValuesToFetch)
            : this._dashboardsFilterDataService.getDashboardFilterValuesByProjectId(currentApp, currentProject, fieldsValuesToFetch);

        filterValuesRequest$
            .subscribe({
                next: (dashboardFilterMenuOptions: FilterMenuOptions) => {
                    if (dashboardFilterMenuOptions) {
                        this.model.dashboardFilterMenuOptions = {
                            ...this.model.dashboardFilterMenuOptions,
                            filterValues: {
                                ...this.model.dashboardFilterMenuOptions.filterValues,
                                ...dashboardFilterMenuOptions.filterValues
                            }
                        };
                    }
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard Filters values', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            })
            .add(() => {
                this._dashboardFiltersValuesLoaded.next(true);
            });
    }

    applyDashboardFilters(app: App, project: Project, dashboardFilterMenuOptions: FilterMenuOptions): void {
        if (dashboardFilterMenuOptions.selectedFields.length === 0) {
            this.clearDashboardFilters();
            return;
        }

        const filteredSitesRequest$ =project.isSummaryProject === true
            ? this._dashboardsFilterDataService.getSummaryProjectSitesByFilterValues(app, project, dashboardFilterMenuOptions)
            : this._dashboardsFilterDataService.getSitesByFilterValues(app, project, dashboardFilterMenuOptions);

        filteredSitesRequest$
            .subscribe({
                next: (filteredSitesIds: string[]) => {
                    this.model.filteredSites = filteredSitesIds;
                    this.model.appliedFiltersCount = dashboardFilterMenuOptions.selectedFields.length;
                    this._dashboardFilteredSitesLoaded.next(true);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard Filters', 'applied'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this.clearDashboardFilters();
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard Filters', 'applying'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    clearDashboardFilters(): void {
        this.model.appliedFiltersCount = null;
        this.model.filteredSites = undefined;
        this._dashboardFiltersCleared.next();
    }

    setAllFields(allIncomingFields: Field[], siteAlias: string): void {
        allIncomingFields = this.setDefaultFilters(allIncomingFields, siteAlias);

        this.model.dashboardFilterMenuOptions.allFields = allIncomingFields;
        this.model.dashboardFilterMenuOptions = { ...this.model.dashboardFilterMenuOptions };
        this.selectedFields = intersectionBy(
            this.selectedFields,
            this.model.dashboardFilterMenuOptions.allFields,
            'id'
        );
    }

    setDefaultFilters(allIncomingFields: Field[], siteAlias: string): Field[] {
        if (!allIncomingFields) {
            allIncomingFields = new Array<Field>();
        }
        const nameField: Field = { id: SiteFieldsNumericIds.SiteName, Name: `${siteAlias} Name`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Text Box', required: false, type: 'string' } };
        const cityField: Field = { id: SiteFieldsNumericIds.City, Name: `${siteAlias} City`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Text Box', required: false, type: 'string' } };
        const stateField: Field = { id: SiteFieldsNumericIds.State, Name: `${siteAlias} State`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Text Box', required: false, type: 'string' } };
        const statusField: Field = { id: SiteFieldsNumericIds.Status, Name: `${siteAlias} Status`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Checkbox', required: false, type: 'boolean' } };

        allIncomingFields.sort((a, b) => (a.Name > b.Name) ? 1 : -1);
        allIncomingFields.unshift(statusField);
        allIncomingFields.unshift(stateField);
        allIncomingFields.unshift(cityField);
        allIncomingFields.unshift(nameField);

        return allIncomingFields;
    }

    filterAvailableFieldsForFilter(unfilteredFields: Field[]): Field[] {
        if (!unfilteredFields) {
            unfilteredFields = new Array<Field>();
        }

        const filteredFields: Field[] = [];

        unfilteredFields.forEach(field => {
            if (field.FieldType!.id == FieldType.CheckBox ||
                field.FieldType!.id == FieldType.DropDown ||
                field.FieldType!.id == FieldType.Number ||
                field.FieldType!.id == FieldType.DateSelect ||
                field.FieldType!.id == FieldType.PointCategory ||
                field.FieldType!.id == FieldType.Formula) {
                filteredFields.push(field);
            }
        });

        return filteredFields;
    }

    setFieldFilter(searchText: string): void {
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.fieldFilterChange(searchText);
    }

    unselectAll(): void {
        this.selectedFields = [];
        this.model.availableFields = [...this.allFields];
    }

    dropSelected(event: CdkDragDrop<Field[], Field[], any>, selectedFieldsList: CdkDropList<Field[]>, currentApp: App, currentProject: Project): void {
        const previousSelectedFieldIds = this.model.dashboardFilterMenuOptions.selectedFields.map(field => field.id);

        this._dragAndDropFieldSelectionUtilityService.handleDragDropEvent(event);

        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateFields(this.model.dashboardFilterMenuOptions.selectedFields, this.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableFields(this.model.dashboardFilterMenuOptions.selectedFields, this.model.availableFields);
        this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;

        const currentSelectedFieldIds = selectedFieldsList.data.map((field: any) => field.id || field);

        const newlyAddedFieldIds = currentSelectedFieldIds.filter(fieldId =>
            !previousSelectedFieldIds.includes(fieldId)
        );

        if (newlyAddedFieldIds.length > 0) {
            const fieldsValuesToFetch: Field[] = this.allFields.filter(field =>
                newlyAddedFieldIds.includes(field.id)
            );

            if (fieldsValuesToFetch.length > 0 && !Object.keys(this.model.dashboardFilterMenuOptions.filterValues).find((filterValue) => filterValue === fieldsValuesToFetch[0].id)) {
                this.getDashboardFilterValuesByProjectId(currentApp, currentProject, fieldsValuesToFetch);
            }
        }

        this.model.dashboardsFiltersForm.patchValue({
            selectedFields: selectedFieldsList.data,
        });
    }

    dropAvailable(event: CdkDragDrop<Field[], Field[], any>, selectedFieldsList: CdkDropList<Field[]>): void {
        this._dragAndDropFieldSelectionUtilityService.handleDragDropEvent(event);

        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.returnGridFieldsToAvailableFields(this.model.dashboardFilterMenuOptions.selectedFields, this.model.dashboardFilterMenuOptions.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateFields(this.model.dashboardFilterMenuOptions.selectedFields, this.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableFields(this.model.dashboardFilterMenuOptions.selectedFields, this.model.availableFields);
        this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;

        this.model.dashboardsFiltersForm.patchValue({
            selectedFields: selectedFieldsList.data,
        });
    }

    isLoadingValues(isLoading: boolean): void {
        this.model.isLoadingValues = isLoading;
    }

}
